import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export interface PzWzDocRef {
  line_id: number;
  document_id: number;
  document_number: string;
  document_type: string;
  document_type_display: string;
  document_date: string;
  document_status: string;
  product_id: number;
  product_name: string;
  product_code: string;
  storage_location_code: string | null;
  quantity?: number;
  allocation_id?: number;
}

export interface PzWzReceipt extends PzWzDocRef {
  received_quantity: number;
  issued_quantity: number;
  remaining_quantity: number;
  outgoing: PzWzDocRef[];
}

export interface PzWzOutgoingLine extends PzWzDocRef {
  quantity: number;
  allocated_quantity: number;
  unallocated_quantity: number;
  sources: PzWzDocRef[];
}

interface PzWzAllocationsPanelProps {
  receipts?: PzWzReceipt[];
  outgoingLines?: PzWzOutgoingLine[];
  unallocatedOutgoing?: PzWzDocRef[];
  loading?: boolean;
  showProductColumn?: boolean;
  emptyMessage?: string;
}

const cellSx = { color: "white", borderColor: "#474747ff" };
const headSx = { color: "text.secondary", borderColor: "#474747ff", fontWeight: 600 };

function typeChipColor(docType: string): "success" | "info" | "warning" | "default" {
  if (docType === "PZ") return "success";
  if (docType === "WZ") return "info";
  if (docType === "KOR") return "warning";
  return "default";
}

const DocLink: React.FC<{ documentId: number; number: string }> = ({ documentId, number }) => {
  const navigate = useNavigate();
  return (
    <Typography
      component="button"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/documents/${documentId}`);
      }}
      sx={{
        color: "#90caf9",
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        font: "inherit",
        textDecoration: "underline",
        textUnderlineOffset: 2,
      }}
    >
      {number}
    </Typography>
  );
};

export const PzWzAllocationsPanel: React.FC<PzWzAllocationsPanelProps> = ({
  receipts = [],
  outgoingLines = [],
  unallocatedOutgoing = [],
  loading = false,
  showProductColumn = false,
  emptyMessage,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const hasContent =
    receipts.length > 0 || outgoingLines.length > 0 || unallocatedOutgoing.length > 0;

  if (!hasContent) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage || t("documents.allocations.empty") || "Brak zależności PZ–WZ"}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {receipts.map((receipt) => (
        <Accordion
          key={`receipt-${receipt.line_id}`}
          disableGutters
          sx={{
            bgcolor: "rgba(255,255,255,0.03)",
            border: "1px solid #474747ff",
            color: "white",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
                width: "100%",
                pr: 1,
              }}
            >
              <Chip
                size="small"
                label={receipt.document_type}
                color={typeChipColor(receipt.document_type)}
                variant="outlined"
                sx={{ color: "white", "& .MuiChip-label": { color: "white" } }}
              />
              <DocLink documentId={receipt.document_id} number={receipt.document_number} />
              <Typography variant="body2" color="text.secondary">
                {receipt.document_date}
              </Typography>
              {showProductColumn && (
                <Typography variant="body2">
                  {receipt.product_name} ({receipt.product_code})
                </Typography>
              )}
              {receipt.storage_location_code && (
                <Typography variant="body2" color="text.secondary">
                  {receipt.storage_location_code}
                </Typography>
              )}
              <Typography variant="body2" sx={{ ml: "auto" }}>
                {t("documents.allocations.received")}: <strong>{receipt.received_quantity}</strong>
                {" · "}
                {t("documents.allocations.issued")}: <strong>{receipt.issued_quantity}</strong>
                {" · "}
                {t("documents.allocations.remaining")}: <strong>{receipt.remaining_quantity}</strong>
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {receipt.outgoing.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("documents.allocations.noOutgoing")}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>{t("documents.columns.documentNumber")}</TableCell>
                      <TableCell sx={headSx}>{t("documents.columns.documentType")}</TableCell>
                      <TableCell sx={headSx}>{t("documents.columns.date")}</TableCell>
                      <TableCell sx={headSx} align="right">
                        {t("documents.columns.quantity")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receipt.outgoing.map((row) => (
                      <TableRow key={row.allocation_id ?? row.line_id} hover>
                        <TableCell sx={cellSx}>
                          <DocLink documentId={row.document_id} number={row.document_number} />
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Chip
                            size="small"
                            label={row.document_type}
                            color={typeChipColor(row.document_type)}
                            variant="outlined"
                            sx={{ color: "white", "& .MuiChip-label": { color: "white" } }}
                          />
                        </TableCell>
                        <TableCell sx={cellSx}>{row.document_date}</TableCell>
                        <TableCell sx={{ ...cellSx }} align="right">
                          {row.quantity}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {outgoingLines.map((line) => (
        <Accordion
          key={`out-${line.line_id}`}
          defaultExpanded
          disableGutters
          sx={{
            bgcolor: "rgba(255,255,255,0.03)",
            border: "1px solid #474747ff",
            color: "white",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
                width: "100%",
                pr: 1,
              }}
            >
              <Chip
                size="small"
                label={line.document_type}
                color={typeChipColor(line.document_type)}
                variant="outlined"
                sx={{ color: "white", "& .MuiChip-label": { color: "white" } }}
              />
              {showProductColumn && (
                <Typography variant="body2">
                  {line.product_name} ({line.product_code})
                </Typography>
              )}
              <Typography variant="body2" sx={{ ml: "auto" }}>
                {t("documents.columns.quantity")}: <strong>{line.quantity}</strong>
                {" · "}
                {t("documents.allocations.allocated")}: <strong>{line.allocated_quantity}</strong>
                {line.unallocated_quantity > 0 && (
                  <>
                    {" · "}
                    {t("documents.allocations.unallocated")}:{" "}
                    <strong>{line.unallocated_quantity}</strong>
                  </>
                )}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {line.sources.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("documents.allocations.noSource")}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>{t("documents.columns.documentNumber")}</TableCell>
                      <TableCell sx={headSx}>{t("documents.columns.documentType")}</TableCell>
                      <TableCell sx={headSx}>{t("documents.columns.date")}</TableCell>
                      <TableCell sx={headSx}>{t("documents.columns.storageLocation")}</TableCell>
                      <TableCell sx={headSx} align="right">
                        {t("documents.columns.quantity")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {line.sources.map((row) => (
                      <TableRow key={row.allocation_id ?? row.line_id} hover>
                        <TableCell sx={cellSx}>
                          <DocLink documentId={row.document_id} number={row.document_number} />
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Chip
                            size="small"
                            label={row.document_type}
                            color={typeChipColor(row.document_type)}
                            variant="outlined"
                            sx={{ color: "white", "& .MuiChip-label": { color: "white" } }}
                          />
                        </TableCell>
                        <TableCell sx={cellSx}>{row.document_date}</TableCell>
                        <TableCell sx={cellSx}>{row.storage_location_code || "—"}</TableCell>
                        <TableCell sx={cellSx} align="right">
                          {row.quantity}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {unallocatedOutgoing.length > 0 && (
        <Alert
          severity="info"
          sx={{
            bgcolor: "rgba(25, 118, 210, 0.12)",
            color: "#fff",
            "& .MuiAlert-message": { color: "#fff", width: "100%" },
            "& .MuiAlert-icon": { color: "#fff" },
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            {t("documents.allocations.unallocatedTitle")}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>{t("documents.columns.documentNumber")}</TableCell>
                  <TableCell sx={headSx}>{t("documents.columns.documentType")}</TableCell>
                  <TableCell sx={headSx}>{t("documents.columns.date")}</TableCell>
                  <TableCell sx={headSx} align="right">
                    {t("documents.columns.quantity")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unallocatedOutgoing.map((row) => (
                  <TableRow key={`unalloc-${row.line_id}`}>
                    <TableCell sx={cellSx}>
                      <DocLink documentId={row.document_id} number={row.document_number} />
                    </TableCell>
                    <TableCell sx={cellSx}>{row.document_type}</TableCell>
                    <TableCell sx={cellSx}>{row.document_date}</TableCell>
                    <TableCell sx={cellSx} align="right">
                      {row.quantity}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Alert>
      )}
    </Box>
  );
};

export default PzWzAllocationsPanel;
